import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AdminStarRating, InterestStatsPanel, RatingsBreakdown } from "../../../components/DealFlow";
import { canApprove } from "../../../utils/dealFlowRoleUtils";
import {
  approveCampaignInitial,
  rejectCampaignInitial,
  updateCampaignType,
  fetchCampaignProducts,
  checkApprovalOutcome,
} from "../../../utils/campaignViewActions";
import {
  toApiDateTime,
  businessDateTimeLocalValue,
  goLiveError as scheduledGoLiveError,
  GO_LIVE_ERROR_COPY,
} from "../../../utils/scheduleDates";
import { rejectionReasonError } from "../../../utils/rejectionReason";

/**
 * InitialApprovalActions — First-time approval workflow
 * Backend: update-campaign-byadmin (superAdmin.controller.js:1830)
 */
const InitialApprovalActions = ({
  canEdit,
  userInfo,
  campaign,
  campaignId,
  setLoading,
  ratingsData,
  myRating,
  onPrint,
  onReload,
}) => {
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const [productsType, setProductsType] = useState([]);
  const [productId, setProductId] = useState();
  const [campaignType, setCampaignType] = useState("");
  // "now" publishes on approval (status 3); "date" sends launchDate, which the
  // API's resolveApprovalSchedule turns into Coming soon (9) until that date.
  const [goLive, setGoLive] = useState("now");
  const [goLiveAt, setGoLiveAt] = useState("");
  const [goLiveError, setGoLiveError] = useState("");
  // Latched once an approval is SAVED but its outcome could not be read back:
  // the decision cannot be offered again (the API would 409) on a stale screen.
  const [decisionSaved, setDecisionSaved] = useState(false);
  const [statusCheck, setStatusCheck] = useState(""); // "" | "checking" | "unreadable"
  // C2: the API's message when the campaign changed after this view was loaded.
  const [staleReview, setStaleReview] = useState("");
  // The issuer's drafts, newest first. null until read (and when the read
  // fails), so nothing below is claimed about a campaign that was not read.
  const [drafts, setDrafts] = useState(null);
  // False once the panel unmounts. Every handler re-checks it after its await:
  // a response that lands after the admin has moved on must neither navigate
  // (yanking them off the page they chose) nor set state here. A layout effect,
  // because React 17 runs useEffect cleanup after the commit: a response landing
  // in that gap would still see the panel as mounted.
  const mounted = useRef(true);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const resolvedCampaignId = campaignId || campaign?.data?.[0]?.id;
  // C2: the version on screen. Every decision sends it, and the API refuses one
  // made on a campaign saved since.
  const contentRevision = campaign?.data?.[0]?.content_revision;
  // Toronto now, rounded up to the minute: the API's clock, not the viewer's.
  const goLiveMin = businessDateTimeLocalValue();
  const rejectReasonError = rejectionReasonError(comment);

  useEffect(() => {
    if (campaign?.data?.[0]?.type) {
      fetchCampaignProducts(campaign.data[0].type).then(setProductsType);
    }
  }, [campaign]);

  // Read as ForReviewActions reads them, and again on every reload of the view.
  // `current` drops a reply that a newer read (or leaving) has overtaken.
  useEffect(() => {
    setDrafts(null);
    const slug = campaign?.data?.[0]?.slug;
    if (!slug) return undefined;
    let current = true;
    axios({
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-campaigndetails-listby-slug",
      headers: { "x-access-token": localStorage.getItem("accessToken") },
      data: { slug, user_id: campaign?.data?.[0]?.user_id, campaign_id: resolvedCampaignId },
    })
      .then((response) => {
        if (current && mounted.current) setDrafts(response.data?.data?.rows || []);
      })
      .catch((error) => {
        console.error("Failed to load the issuer's drafts", error);
      });
    return () => { current = false; };
  }, [campaign, resolvedCampaignId]);

  // The issuer's note on resubmission (campaign_drafts.comment): the newest
  // draft that carries one.
  const issuerNote = (drafts || []).map((d) => String(d?.comment ?? "").trim()).find(Boolean) || "";
  // Q-07: the proof of bank account on the campaign's payout row, the record
  // submitCampaign itself checks, served by the api as data[0].has_bank_proof
  // (a boolean; the document URL is not sent). The newest draft snapshot is not
  // read for this: one written before the release has no bankdoc key, and an
  // in-review bank save leaves an empty one while the proof stays on file
  // (review, 21 Oct). An api that does not send the field claims nothing.
  const bankProofMissing = campaign?.data?.[0]?.has_bank_proof === false;

  // QA 2026-07-28 F-1: no setLoading around these two. Both open a confirmation
  // dialog and await the operator's click; the page-level loader (z 99999) sat
  // above the Swal (z 1060) and swallowed it. `confirmAction` already renders
  // SweetAlert's own inline spinner for the network phase via preConfirm +
  // showLoaderOnConfirm, so nothing is lost.
  const handleApprove = async () => {
    if (decisionSaved) return;
    const options = { comment, productId, contentRevision };
    if (goLive === "date") {
      if (!goLiveAt) {
        setGoLiveError("Choose the date and time it should go live, or select Now.");
        return;
      }
      // Compared as INSTANTS against Toronto now + the lead time; a time in a
      // DST change or one the API format cannot hold is refused. A moment that
      // is not in the future would publish now, not be Coming soon.
      const problem = scheduledGoLiveError(goLiveAt);
      if (problem) {
        setGoLiveError(problem);
        return;
      }
      // "On a date" never sends a request without a launchDate it can use.
      const launchDate = toApiDateTime(goLiveAt);
      if (!launchDate) {
        setGoLiveError(GO_LIVE_ERROR_COPY.invalid);
        return;
      }
      options.launchDate = launchDate;
    }
    setGoLiveError("");
    const result = await approveCampaignInitial(resolvedCampaignId, options);
    if (!mounted.current) return;
    if (result.success && result.navigateTo) navigate(result.navigateTo);
    else if (result.success) setDecisionSaved(true);
    else if (result.staleReview) setStaleReview(result.staleReview);
  };

  const handleReload = () => {
    setStaleReview("");
    onReload();
  };

  // Retries ONLY the read-back, never the decision, and follows what it finds.
  const handleCheckStatus = async () => {
    setStatusCheck("checking");
    const outcome = await checkApprovalOutcome(resolvedCampaignId);
    if (!mounted.current) return;
    if (outcome.navigateTo) {
      navigate(outcome.navigateTo);
      return;
    }
    setStatusCheck("unreadable");
  };

  const handleReject = async () => {
    if (rejectReasonError || decisionSaved) return;
    const result = await rejectCampaignInitial(resolvedCampaignId, { comment, contentRevision });
    if (!mounted.current) return;
    if (result.success && result.navigateTo) navigate(result.navigateTo);
    else if (result.staleReview) setStaleReview(result.staleReview);
  };

  // Keeps its loader: updateCampaignType opens no dialog, so here the overlay
  // wraps only the network call - which is what it is for.
  const handleCampaignTypeSubmit = async () => {
    setLoading(true);
    await updateCampaignType(resolvedCampaignId, campaignType);
    setLoading(false);
  };

  return (
    <div className="left">
      <div className="title-topSection">
        <h1 className="title">Campaign Review</h1>
        <button onClick={onPrint} className="approveButton downloadbtn">
          Download
        </button>
      </div>

      {/* Star Rating */}
      {resolvedCampaignId && (
        <div style={{ marginBottom: "16px" }}>
          <AdminStarRating
            campaignId={resolvedCampaignId}
            initialRating={myRating?.rating || 0}
            initialNotes={myRating?.notes || ""}
          />
          <RatingsBreakdown ratings={ratingsData} loading={!ratingsData} />
          <InterestStatsPanel
            campaignId={resolvedCampaignId}
            currency={userInfo?.currency_type || "CAD"}
          />
        </div>
      )}

      {issuerNote && (
        <div className="detailItem" style={{ marginBottom: "12px" }} data-testid="issuer-note">
          <span className="itemKey">Issuer&apos;s note:</span>
          <span className="itemValue">{issuerNote}</span>
        </div>
      )}
      {bankProofMissing && (
        <div className="detailItem" style={{ marginBottom: "12px" }} data-testid="bank-proof-missing">
          <span className="itemKey">Proof of bank account:</span>
          <span className="itemValue">missing</span>
        </div>
      )}

      {/* Product Type Selection */}
      {productsType.length > 0 && (
        <div className="detailItem" style={{ marginBottom: "12px" }}>
          <span className="itemKey">Product Type:</span>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="itemValue"
          >
            <option value="">Select Product</option>
            {productsType.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.product_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Comment. On a rejection it is the reason shown to the issuer, so it is
          required then (the API refuses one under 20 characters). Must stay the
          first textarea.itemValue: e2e specs select it that way. */}
      <div className="detailItem" style={{ marginBottom: "12px" }}>
        <span className="itemKey">Comment (optional to approve; required to reject, at least 20 characters, shown to the issuer):</span>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="itemValue"
          rows={3}
          placeholder="Required to reject. Shown to the issuer."
          style={{ width: "100%", padding: "8px" }}
        />
        {rejectReasonError && canApprove() && canEdit !== false && (
          <div className="field__hint" data-testid="reject-reason-hint">
            {rejectReasonError}
          </div>
        )}
      </div>

      {/* Approve/Reject */}
      {canApprove() && canEdit !== false ? (
        <div style={{ marginBottom: "16px" }}>
          {/* Go-live choice. "Now" is the default and sends the same body as
              before; "On a date" sends launchDate as the API's Toronto
              wall-clock string. No itemValue class anywhere in here. */}
          <fieldset className="detailItem" style={{ border: "none", padding: 0, margin: "0 0 12px" }}>
            <legend className="itemKey">When should it go live?</legend>
            <label style={{ marginRight: "16px" }}>
              <input
                type="radio"
                name="goLive"
                value="now"
                checked={goLive === "now"}
                onChange={() => { setGoLive("now"); setGoLiveError(""); }}
                disabled={decisionSaved}
                data-testid="golive-now"
              />{" "}
              Now
            </label>
            <label>
              <input
                type="radio"
                name="goLive"
                value="date"
                checked={goLive === "date"}
                onChange={() => setGoLive("date")}
                disabled={decisionSaved}
                data-testid="golive-date"
              />{" "}
              On a date
            </label>
            {goLive === "date" && (
              <div style={{ marginTop: "8px" }}>
                <input
                  type="datetime-local"
                  className="field__input"
                  style={{ maxWidth: "280px" }}
                  value={goLiveAt}
                  min={goLiveMin}
                  onChange={(e) => { setGoLiveAt(e.target.value); setGoLiveError(""); }}
                  disabled={decisionSaved}
                  aria-label="Go-live date and time"
                  data-testid="golive-datetime"
                />
                <div className="field__hint" data-testid="golive-hourly-hint">
                  Campaigns go live at the first hourly check after the chosen time (Toronto time).
                </div>
              </div>
            )}
            {goLiveError && (
              <div className="field__error" role="alert" style={{ marginTop: "8px" }} data-testid="golive-error">
                {goLiveError}
              </div>
            )}
          </fieldset>
          {decisionSaved && (
            <div role="status" style={{ margin: "0 0 12px" }} data-testid="decision-saved-note">
              <p style={{ margin: "0 0 8px" }}>
                The decision was saved. Its final status could not be read; refresh the page to see it.
              </p>
              <button
                type="button"
                className="approveButton"
                onClick={handleCheckStatus}
                disabled={statusCheck === "checking"}
              >
                Check status
              </button>
              {statusCheck === "unreadable" && (
                <span className="field__hint" style={{ marginLeft: "8px" }} data-testid="check-status-unreadable">
                  It still could not be read. Try again, or refresh the page.
                </span>
              )}
            </div>
          )}
          {staleReview && (
            <div role="alert" style={{ margin: "0 0 12px" }} data-testid="stale-review">
              <p className="field__error" style={{ margin: "0 0 8px" }}>{staleReview}</p>
              {onReload && (
                <button type="button" className="approveButton" onClick={handleReload}>
                  Reload
                </button>
              )}
            </div>
          )}
          <p>You can click on Approve / Reject button below to respond.</p>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button
              className="approveButton"
              onClick={handleApprove}
              disabled={decisionSaved}
              style={decisionSaved ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              Approve
            </button>
            <button
              className="approveButton"
              onClick={handleReject}
              disabled={!!rejectReasonError || decisionSaved}
              style={{ background: "#d33", ...(rejectReasonError || decisionSaved ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
            >
              Reject
            </button>
          </div>
        </div>
      ) : (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          As an Evaluator, you can rate campaigns but cannot approve or reject them.
        </p>
      )}

      {/* Campaign Type */}
      <div className="detailItem" style={{ marginTop: "12px" }}>
        <span className="itemKey">Campaign Type:</span>
        <input
          type="text"
          value={campaignType}
          onChange={(e) => setCampaignType(e.target.value)}
          className="itemValue"
          placeholder="Enter campaign type"
          style={{ padding: "6px", marginRight: "8px" }}
        />
        {canEdit !== false && (
        <button className="approveButton" onClick={handleCampaignTypeSubmit}>
          Update Type
        </button>
        )}
      </div>
    </div>
  );
};

export default InitialApprovalActions;
